import { FeatureFlag, OddsFormat, Size as ScreenSize } from '@ps/web-core-enums';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation, useScreenSize, useTrackingLabel } from '@ps/web-shared-components';
import React, { useContext } from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import Radio from '@swf/sports/design-system/components/Radio';
import RadioGroup from '@swf/sports/design-system/components/RadioGroup';
import { OddsFormatContext } from '@swf/sports/providers/OddsFormatProvider';

import {
  TXTWCL_American,
  TXTWCL_Decimal,
  TXTWCL_Fractional,
  TXTWCL_Odds_Type
} from './OddsFormatSelector.messages.json';
import styles from './OddsFormatSelector.scss';

const OddsFormatSelector: React.FC = () => {
  const { oddsFormat, setOddsFormat } = useContext(OddsFormatContext);
  const { inputFilled } = useTrackers();

  const fractionalTrackingLabel = useTrackingLabel(TXTWCL_Fractional);
  const decimalTrackingLabel = useTrackingLabel(TXTWCL_Decimal);
  const americanTrackingLabel = useTrackingLabel(TXTWCL_American);
  const oddsTypeTrackingLabel = useTrackingLabel(TXTWCL_Odds_Type);

  const { formatMessage } = useRegulatoryIntl();
  const isAmericanOddsSupported = useFeature(FeatureFlag.ISPAmericanOdds);
  const { greaterThan } = useScreenSize();
  const isMobile = !greaterThan(ScreenSize.Small);

  const configs = [
    {
      key: 'decimal',
      oddsFormat: OddsFormat.Decimal,
      trackingLabel: decimalTrackingLabel
    },
    {
      key: 'fractional',
      oddsFormat: OddsFormat.Fractional,
      trackingLabel: fractionalTrackingLabel
    },
    {
      key: 'american',
      oddsFormat: OddsFormat.American,
      trackingLabel: americanTrackingLabel
    }
  ];

  const handleOnChange = (value: string) => {
    const selectedConfig = configs.find((config) => config.key === value) || configs[0];

    setOddsFormat(selectedConfig.oddsFormat);
    inputFilled({
      fieldName: oddsTypeTrackingLabel,
      fieldType: 'radio',
      strInput: selectedConfig.key
    });
  };

  const selectedValue =
    configs.find((config) => config.oddsFormat === oddsFormat)?.key || 'decimal';

  return (
    <RadioGroup
      orientation={isMobile ? 'vertical' : 'horizontal'}
      onChange={handleOnChange}
      value={selectedValue}
      aria-label={formatMessage(TXTWCL_Odds_Type)}
    >
      <Radio value="fractional" className={styles.oddsFormat}>
        <Translation {...TXTWCL_Fractional} />
      </Radio>
      <Radio value="decimal" className={styles.oddsFormat}>
        <Translation {...TXTWCL_Decimal} />
      </Radio>
      {isAmericanOddsSupported && (
        <Radio value="american" className={styles.oddsFormat}>
          <Translation {...TXTWCL_American} />
        </Radio>
      )}
    </RadioGroup>
  );
};

export default OddsFormatSelector;
